import React, {
  MouseEvent,
  ReactElement,
  cloneElement,
  ReactNode,
  FocusEvent,
  FC,
  useRef,
  useContext,
  useLayoutEffect,
} from "react";
import classNames from "classnames";
import { OptionMenuClassName, OptionMenuContext } from "./OptionMenu";
import { Tick, Delete } from "../../icons";
import { OptionMenuItemsContext } from "./OptionMenuItemsContext";

export interface IOptionMenuItemProps {
  destructive?: boolean;
  active?: boolean;
  disabled?: boolean;
  selected?: boolean;
  icon?: ReactElement;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => any;
  onMouseEnter?: (event: MouseEvent<HTMLButtonElement>) => any;
  onMouseLeave?: (event: MouseEvent<HTMLButtonElement>) => any;
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => any;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => any;
  className?: string;
  closeOnClick?: boolean;
  children?: ReactNode;
  ["aria-describedby"]?: string;
}

const OptionMenuItem: FC<IOptionMenuItemProps> = ({
  closeOnClick = true,
  disabled,
  onClick,
  destructive,
  selected,
  icon,
  className,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ["aria-describedby"]: ariaDescribedBy,
}) => {
  const menuItemRef = useRef<HTMLButtonElement>(null);
  const { registerMenuItem, unregisterMenuItem, menuRef } = useContext(OptionMenuItemsContext);
  const { close, buttonRef } = useContext(OptionMenuContext);

  useLayoutEffect(() => {
    if (!disabled) {
      registerMenuItem(menuItemRef);
    }

    return () => {
      unregisterMenuItem(menuItemRef);
    };
  }, [disabled]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    if (closeOnClick) {
      close?.();
    }

    // Triggered by keyboard
    if (event.nativeEvent.detail === 0 && closeOnClick) {
      buttonRef.current?.focus();
    }

    onClick?.(event);
  };

  const handleMouseUp = (): void => {
    // Focus the menu to prevent showing focus rings on click while also
    // preventing an outer blur event causing the menu to close
    menuRef.current?.focus();
  };

  return (
    <button
      className={classNames(
        `${OptionMenuClassName}__item`,
        { [`${OptionMenuClassName}__item--destructive`]: destructive },
        className,
      )}
      onClick={!disabled ? handleClick : undefined}
      role={selected !== undefined ? "menuitemradio" : "menuitem"}
      aria-checked={selected}
      disabled={disabled}
      ref={menuItemRef}
      onMouseUp={handleMouseUp}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      aria-describedby={ariaDescribedBy}
    >
      {!!icon && cloneElement(icon, { className: `${OptionMenuClassName}__icon` })}
      <span className={`${OptionMenuClassName}__text`}>{children}</span>
      {selected && <Tick className={`${OptionMenuClassName}__tick`} />}
      {destructive && <Delete className={`${OptionMenuClassName}__delete`} />}
    </button>
  );
};

export default OptionMenuItem;
